import {
	Search,
	CalendarDays,
	LayoutGrid,
	List,
	Maximize2,
	Minimize2,
	Rows3,
	Columns3,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
	SearchableSelect,
	type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { TaskView } from "@/hooks/useTaskFilters";
import { type Project } from "@/services/project.service";
import { type Sprint } from "@/services/sprint.service";
import { type Profile } from "@/services/profile.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import { UserMultiSelect } from "./UserMultiSelect";
import { SYSTEM_STAGES } from "./system-stages";

/** Warms the lazy calendar chunk on intent, so the click feels instant. */
const prefetchCalendar = () => {
	void import("./calendar/CalendarView");
};

export interface TaskFiltersProps {
	className?: string;
	projects: Project[];
	profiles: Profile[];
	sprints: Sprint[];
	sprintsLoading: boolean;
	view: TaskView;
	expanded: boolean;
	grouped: boolean;
	filterProject: string;
	filterSprint: string;
	filterUserIds: string[];
	filterStatus: string;
	search: string;
	isFiltered: boolean;
	stages?: WorkflowStage[];
	onFilterProjectChange: (v: string) => void;
	onFilterSprintChange: (v: string) => void;
	onFilterUserIdsChange: (ids: string[]) => void;
	onFilterStatusChange: (v: string) => void;
	onSearchChange: (v: string) => void;
	onViewChange: (v: TaskView) => void;
	onToggleExpand: () => void;
	onToggleGroup: () => void;
	onClearFilters: () => void;
}

export function TaskFilters({
	className,
	projects,
	profiles,
	sprints,
	sprintsLoading,
	view,
	expanded,
	grouped,
	filterProject,
	filterSprint,
	filterUserIds,
	filterStatus,
	search,
	isFiltered,
	stages,
	onFilterProjectChange,
	onFilterSprintChange,
	onFilterUserIdsChange,
	onFilterStatusChange,
	onSearchChange,
	onViewChange,
	onToggleExpand,
	onToggleGroup,
	onClearFilters,
}: TaskFiltersProps) {
	return (
		<div
			className={cn("flex flex-wrap items-center gap-3 mb-6", className)}
		>
			{/* Project */}
			<div className="w-48">
				<SearchableSelect
					value={filterProject}
					onValueChange={(v) => onFilterProjectChange(v || "all")}
					placeholder="All Projects"
					options={[
						{ value: "all", label: "All Projects" },
						...projects.map<SearchableSelectOption>((p) => ({
							value: p.id,
							label: p.name,
						})),
					]}
				/>
			</div>

			{/* Sprint */}
			<div className="w-44">
				<SearchableSelect
					value={filterSprint}
					onValueChange={(v) => onFilterSprintChange(v || "all")}
					disabled={sprintsLoading}
					loading={sprintsLoading}
					placeholder={sprintsLoading ? "Loading..." : "All Sprints"}
					options={[
						{ value: "all", label: "All Sprints" },
						{ value: "none", label: "Unassigned" },
						...sprints.map<SearchableSelectOption>((s) => ({
							value: s.id,
							label: s.name,
						})),
					]}
				/>
			</div>

			{/* Search */}
			<div className="relative w-full sm:w-auto">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
				<Input
					placeholder="Search tasks..."
					value={search}
					onChange={(e) => onSearchChange(e.target.value)}
					className="pl-8 w-full h-9 text-sm sm:w-56"
				/>
			</div>

			{/* User */}
			<div className="min-w-44 max-w-xs">
				<UserMultiSelect
					profiles={profiles}
					value={filterUserIds}
					onValueChange={onFilterUserIdsChange}
					placeholder="All Users"
					includeUnassigned
				/>
			</div>

			{/* Status (board groups by stage already, so it is redundant there) */}
			{view !== "board" && (
				<div className="w-40">
					<SearchableSelect
						value={filterStatus}
						onValueChange={(v) => onFilterStatusChange(v || "all")}
						placeholder="All Statuses"
						options={[
							{ value: "all", label: "All Statuses" },
							...(
								stages ?? SYSTEM_STAGES
							).map<SearchableSelectOption>((stage) => ({
								value: stage.key,
								label: stage.name,
							})),
						]}
					/>
				</div>
			)}

			{/* Clear filters */}
			{isFiltered && (
				<button
					onClick={onClearFilters}
					className="text-xs! text-muted hover:text-foreground "
				>
					Clear filters
				</button>
			)}

			{/* View toggle */}
			<div className="ml-auto flex items-center border border-border rounded-lg overflow-hidden bg-surface">
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							onClick={() => onViewChange("board")}
							className={cn(
								"p-2 transition-colors",
								view === "board"
									? "bg-primary text-primary-foreground"
									: "text-muted hover:text-foreground hover:bg-muted-subtle",
							)}
						>
							<LayoutGrid className="h-4 w-4" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="bottom">Board view</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							onClick={() => onViewChange("list")}
							className={cn(
								"p-2 transition-colors",
								view === "list"
									? "bg-primary text-primary-foreground"
									: "text-muted hover:text-foreground hover:bg-muted-subtle",
							)}
						>
							<List className="h-4 w-4" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="bottom">List view</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							onClick={() => onViewChange("calendar")}
							onMouseEnter={prefetchCalendar}
							onFocus={prefetchCalendar}
							className={cn(
								"p-2 transition-colors",
								view === "calendar"
									? "bg-primary text-primary-foreground"
									: "text-muted hover:text-foreground hover:bg-muted-subtle",
							)}
						>
							<CalendarDays className="h-4 w-4" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="bottom">Calendar view</TooltipContent>
				</Tooltip>
				{view === "board" && (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								onClick={onToggleGroup}
								className={cn(
									"p-2 transition-colors border-l border-border",
									grouped
										? "text-primary hover:bg-muted-subtle"
										: "text-muted hover:text-foreground hover:bg-muted-subtle",
								)}
							>
								{grouped ? (
									<Rows3 className="h-4 w-4" />
								) : (
									<Columns3 className="h-4 w-4" />
								)}
							</button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							{grouped ? "Ungroup (columns)" : "Group by project"}
						</TooltipContent>
					</Tooltip>
				)}
				{view === "board" && (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								onClick={onToggleExpand}
								className="p-2 text-muted hover:text-foreground hover:bg-muted-subtle transition-colors border-l border-border"
							>
								{expanded ? (
									<Minimize2 className="h-4 w-4 rotate-45" />
								) : (
									<Maximize2 className="h-4 w-4 rotate-45" />
								)}
							</button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							{expanded ? "Contract" : "Expand"}
						</TooltipContent>
					</Tooltip>
				)}
			</div>
		</div>
	);
}
